import { DEFAULT_THRESHOLDS, evaluateRecognition, evaluateHandwritingRecognition, PREVIEW_WORDS } from './previewConfig';

// The product's ANNE/NANE words are added to laboratory evaluation ONLY.
// Do not change the existing 11-word prototype pool or live G12 scoring.
export const BENCHMARK_WORDS = Object.freeze([...PREVIEW_WORDS, 'ANNE', 'NANE']);
const G12_WORDS = new Set(['ANNE', 'NANE']);
export function benchmarkDecision(target, text, confidence, thresholds = DEFAULT_THRESHOLDS) {
  if (!BENCHMARK_WORDS.includes(target)) return 'uncertain';
  return G12_WORDS.has(target)
    ? evaluateHandwritingRecognition(target, text, confidence, thresholds)
    : evaluateRecognition(target, text, confidence, thresholds);
}

export const BENCHMARK_SCHEMA = 'dandu-handwriting-benchmark-v1';
export const DECISIONS = ['correct', 'retry', 'uncertain', 'error'];
export const SAMPLE_FIELDS = ['sampleId', 'writerId', 'partition', 'source', 'target', 'actualText', 'humanLabel',
  'ocrText', 'confidence', 'decision', 'model', 'modelVersion', 'latencyMs', 'modelLoadMs', 'coldStart',
  'device', 'inputTypes', 'browser', 'testedAt', 'commitSha', 'thresholds'];

export const anonymousId = (prefix) => `${prefix}-${window.crypto.randomUUID()}`;
export function humanLabel(target, actualText, unreadable) {
  if (unreadable) return 'unreadable';
  // Same safe alphabet as the preview. I/i/ı/İ and punctuation stay out of V1.
  if (!/^[A-HJ-Za-hj-z]{1,24}$/.test(actualText)) return null;
  return actualText.toUpperCase() === target ? 'correct' : 'incorrect';
}
export const lengthBand = (word) => word.length >= 5 ? '5+' : String(word.length);
const finiteTimes = (values) => values.filter(v => Number.isFinite(v) && v >= 0).sort((a, b) => a - b);
export function timing(values, total) {
  const v = finiteTimes(values), n = v.length;
  return { n, total, mean: n ? v.reduce((a, b) => a + b, 0) / n : null,
    median: n ? (v[Math.floor((n - 1) / 2)] + v[Math.floor(n / 2)]) / 2 : null,
    p95: n ? v[Math.ceil(n * .95) - 1] : null };
}
const ratio = (numerator, denominator) => ({ numerator, denominator, value: denominator ? numerator / denominator : null });
export function decisionFor(sample, thresholds) {
  return sample.decision === 'error' ? 'error' : thresholds
    ? benchmarkDecision(sample.target, sample.ocrText, sample.confidence, thresholds) : sample.decision;
}
export function benchmarkMetrics(samples, thresholds = null) {
  const readable = samples.filter(s => ['correct', 'incorrect'].includes(s.humanLabel));
  const correct = readable.filter(s => s.humanLabel === 'correct');
  const incorrect = readable.filter(s => s.humanLabel === 'incorrect');
  const count = (rows, decision) => rows.filter(s => decisionFor(s, thresholds) === decision).length;
  const matrix = Object.fromEntries(['correct', 'incorrect'].map(label => [label,
    Object.fromEntries(DECISIONS.map(d => [d, count(readable.filter(s => s.humanLabel === label), d)]))]));
  return { total: samples.length, labeled: samples.filter(s => ['correct', 'incorrect', 'unreadable'].includes(s.humanLabel)).length,
    correct: correct.length, incorrect: incorrect.length, unreadable: samples.filter(s => s.humanLabel === 'unreadable').length,
    missingLabel: samples.filter(s => !['correct', 'incorrect', 'unreadable'].includes(s.humanLabel)).length,
    errors: count(samples, 'error'), readableErrors: count(readable, 'error'),
    acceptance: ratio(count(correct, 'correct'), correct.length),
    falseAcceptance: ratio(count(incorrect, 'correct'), incorrect.length),
    falseRetry: ratio(count(correct, 'retry'), correct.length),
    uncertain: ratio(count(readable, 'uncertain'), readable.length),
    exact: ratio(readable.filter(s => s.decision !== 'error' && s.ocrText === s.actualText).length, readable.length),
    coverage: ratio(count(readable, 'correct') + count(readable, 'retry'), readable.length),
    latency: timing(samples.map(s => s.latencyMs), samples.length),
    load: timing(samples.filter(s => s.coldStart).map(s => s.modelLoadMs), samples.filter(s => s.coldStart).length),
    matrix, falseAccepted: incorrect.filter(s => decisionFor(s, thresholds) === 'correct') };
}
export function breakdown(samples, field, thresholds) {
  const keys = field === 'length' ? ['2', '3', '4', '5+'] : [...new Set(samples.map(s => s[field]))].sort();
  return keys.map(key => ({ key, ...benchmarkMetrics(samples.filter(s => (field === 'length' ? lengthBand(s.target) : s[field]) === key), thresholds) }));
}
export function safeSample(sample) {
  return Object.fromEntries(SAMPLE_FIELDS.map(key => [key, key === 'thresholds'
    ? { match: sample.thresholds?.match ?? null, mismatch: sample.thresholds?.mismatch ?? null }
    : sample[key] ?? null]));
}
export function benchmarkJson(samples, simulation = DEFAULT_THRESHOLDS) {
  const safe = samples.map(safeSample);
  return JSON.stringify({ schema: BENCHMARK_SCHEMA, exportedAt: new Date().toISOString(),
    privacy: 'Local-only metadata. No images, strokes, student records or full user-agent.',
    samples: safe, reports: ['adult', 'synthetic'].map(source => ({ source,
      recorded: benchmarkMetrics(safe.filter(s => s.source === source)),
      simulation: { thresholds: simulation, metrics: benchmarkMetrics(safe.filter(s => s.source === source), simulation) } })) }, null, 2);
}
const csvCell = value => {
  let text = value == null ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value);
  // Prevent spreadsheet formula injection in OCR output, including whitespace prefixes.
  if (/^\s*[=+@-]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};
export function samplesCsv(samples) {
  return [SAMPLE_FIELDS, ...samples.map(s => SAMPLE_FIELDS.map(key => safeSample(s)[key]))]
    .map(row => row.map(csvCell).join(',')).join('\r\n');
}
export function metricsCsv(samples, thresholds = null) {
  const rows = [['source', 'partition', 'group', 'value', 'metric', 'numerator', 'denominator', 'rate', 'mean_ms', 'median_ms', 'p95_ms', 'mode']];
  for (const source of ['adult', 'synthetic']) for (const partition of ['all', 'calibration', 'validation']) {
    const selected = samples.filter(s => s.source === source && (partition === 'all' || s.partition === partition));
    const groups = [{ group: 'all', key: 'all', ...benchmarkMetrics(selected, thresholds) },
      ...['target', 'length', 'device', 'inputTypes'].flatMap(field => breakdown(selected, field, thresholds).map(m => ({ group: field, ...m })))];
    for (const m of groups) {
      for (const metric of ['acceptance', 'falseAcceptance', 'falseRetry', 'uncertain', 'exact', 'coverage']) {
        const r = m[metric]; rows.push([source, partition, m.group, m.key, metric, r.numerator, r.denominator, r.value, '', '', '', thresholds ? JSON.stringify(thresholds) : 'recorded']);
      }
      for (const metric of ['latency', 'load']) { const t = m[metric]; rows.push([source, partition, m.group, m.key, metric, t.n, t.total, '', t.mean, t.median, t.p95, thresholds ? JSON.stringify(thresholds) : 'recorded']); }
      for (const metric of ['total', 'labeled', 'correct', 'incorrect', 'unreadable', 'missingLabel', 'errors', 'readableErrors']) rows.push([source, partition, m.group, m.key, metric, m[metric], m.total, '', '', '', '', 'counts']);
      for (const human of ['correct', 'incorrect']) for (const decision of DECISIONS) rows.push([source, partition, m.group, m.key, `matrix_${human}_${decision}`, m.matrix[human][decision], m[human], '', '', '', '', thresholds ? JSON.stringify(thresholds) : 'recorded']);
    }
  }
  return rows.map(row => row.map(csvCell).join(',')).join('\r\n');
}
export function browserCategory(ua = navigator.userAgent) {
  const match = ['Edg', 'Firefox', 'Chrome', 'Version'].map(family => ua.match(new RegExp(`(${family})/(\\d+)`))).find(Boolean);
  return match ? `${match[1] === 'Version' ? 'Safari' : match[1]} ${match[2]}` : 'other';
}
export function downloadLocal(name, content, type) {
  const url = URL.createObjectURL(new Blob([type.includes('csv') ? '\uFEFF' : '', content], { type }));
  const link = document.createElement('a'); link.href = url; link.download = name; link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
